import { type BenchmarkSummary, BenchmarksControllerApiFactory } from "@eu.mdotm.api/sphere";
import { getApiGen } from "$root/api/factory";
import { IconWalls } from "$root/components/IconWall";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { useEventBus } from "$root/event-bus";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import InstrumentTagClassificationSelect from "$root/functional-areas/instruments/instrument-classification/InstrumentTagClassificationSelect";
import { createInstrumentList } from "$root/functional-areas/instruments/repository";
import { referenceRepository } from "$root/functional-areas/portfolio/references-repository";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { downloadContentDisposition, objMatchFn } from "$root/utils";
import { useMappedQueries } from "$root/third-party-integrations/react-query/hooks";
import { BenchmarkContext } from "$root/widgets-architecture/contexts/benchmark";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { Icon, Searchable, type StylableProps } from "@mdotm/shadcn/legacy/components";
import { TextInput, DropdownMenu } from "@mdotm/shadcn/mdotm/components";
import { type ContextContent, withContext } from "@mdotm/shadcn/legacy/react-extensions";
import { noop } from "@mdotm/shadcn/legacy/utils";
import type { AgGridReact } from "ag-grid-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";

const BenchmarkComposition = (props: ContextContent<typeof BenchmarkContext>) => {
	const { benchmarkId } = props;

	const queryBenchmarkInstrumentsWithInstrumentMap = referenceRepository.benchmarkInstrumentsWithInstrumentMap.useQuery(
		benchmarkId!,
	);
	const queryBenchmarkSummary = referenceRepository.benchmarkSummary.useQuery(benchmarkId!);

	useEventBus("benchmark-update", {
		filter: objMatchFn({ uuid: benchmarkId }),
		listener: () => {
			queryBenchmarkSummary.refetch().catch(noop);
			queryBenchmarkInstrumentsWithInstrumentMap.refetch().catch(noop);
		},
	});

	const query = useMappedQueries(
		([{ composition, userInstrumentsMap }, summary]) => {
			return { summary, instruments: createInstrumentList({ composition, userInstrumentsMap }) };
		},
		[],
		[queryBenchmarkInstrumentsWithInstrumentMap, queryBenchmarkSummary],
	);

	return (
		<ReactQueryWrapperBase
			query={query}
			loadingFallback={<IconWalls.CalculatingApi />}
			errorFallback={<IconWalls.ErrorData />}
		>
			{(response) => <BenchmarkCompositionInner {...response} benchmarkId={benchmarkId!} />}
		</ReactQueryWrapperBase>
	);
};

function BenchmarkCompositionInner({
	instruments,
	summary,
	benchmarkId,
}: {
	summary: BenchmarkSummary;
	instruments: Instrument[];
	benchmarkId: string;
}) {
	const [agGridEl, setAgGridEl] = useState<AgGridReact<Instrument> | null>(null);
	const [groupByClassificationUuid, setGroupByClassificationUuid] = agExtra.useGroupByClassification(agGridEl);
	const columns = columnsDefinition.useInstrumentComposition("read");
	const { t } = useTranslation();

	useWidgetOptions(
		() => ({
			title: "Composition",
			tooltip: t("COMPOSITION.TOOLTIP"),
			actionHeader: function Download() {
				return (
					<>
						<InstrumentTagClassificationSelect
							value={groupByClassificationUuid ?? null}
							onChange={setGroupByClassificationUuid}
						/>
						<DropdownMenu<StylableProps>
							extraTriggerProps={{ className: "self-end" }}
							Trigger={WidgetDownloadTrigger}
							actions={[
								{
									icon: "xls",
									disabled: summary.status === "CALCULATING",
									onClickAsync: () => handleDownloadErrorReport(benchmarkId),
									label: "Benchmark composition",
								},
							]}
						/>
					</>
				);
			},
		}),
		[summary.status, t, groupByClassificationUuid, setGroupByClassificationUuid, benchmarkId],
	);

	const persistColProps = agExtra.usePersistColState(persistedColStateKeys["tableV3-reference-composition-read"]);

	return (
		<Searchable extractFn={(item) => Object.values(item).join(" ")} collection={instruments ?? []}>
			{({ filtered, query, setQuery }) => (
				<div className="min-h-0 grow flex flex-col gap-4">
					<TextInput
						value={query}
						placeholder="Search for instruments name, identifier o asset class"
						onChangeText={setQuery}
						leftContent={<Icon icon="Search" />}
					/>

					<AgGridWithModules<Instrument>
						{...agExtra.aggFunctions()}
						{...persistColProps}
						innerRef={setAgGridEl}
						columnDefs={columns}
						rowData={filtered}
						getRowId={(x) => x.data.tickerName!}
					/>
				</div>
			)}
		</Searchable>
	);
}

async function handleDownloadErrorReport(benchmarkId: string) {
	try {
		const benchmarkApi = getApiGen(BenchmarksControllerApiFactory);
		const response = await benchmarkApi.exportComposition1(benchmarkId, { responseType: "blob" });
		await downloadContentDisposition(response);
	} catch (error) {
		console.error(error);
		throw error;
	}
}

export default withContext(BenchmarkContext)(BenchmarkComposition);
